import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!supabaseUrl) {
  throw new Error('Missing REACT_APP_SUPABASE_URL environment variable. Please check your .env file.');
}

if (!supabaseAnonKey) {
  throw new Error('Missing REACT_APP_SUPABASE_ANON_KEY environment variable. Please check your .env file.');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    flowType: 'pkce'
  }
});

/**
 * Races a promise against a timeout (2026-09-20). Found live: App.tsx's
 * auth gate calls `supabase.auth.getUser()` -- a real network round-trip
 * to Supabase Auth, not a local read -- to confirm a cached session's
 * account still exists server-side. That call had no timeout, so on a
 * slow/unresponsive network it hung forever and the whole app sat on
 * "Loading..." indefinitely on every refresh and right after login (which
 * does a full page reload). Used to wrap exactly that call, not a
 * general-purpose replacement for every getUser()/getSession() call in
 * the app.
 */
export function withTimeout<T>(promise: Promise<T>, ms: number, timeoutMessage = 'Request timed out'): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(timeoutMessage)), ms);
    promise.then(
      (value) => { clearTimeout(timer); resolve(value); },
      (error) => { clearTimeout(timer); reject(error); }
    );
  });
}
